import '../styles/ApplyApplicationPanel.css';
import React, { useRef, useState } from 'react';
import { AlertCircle, ArrowRight, Check, RotateCw } from 'lucide-react';
import { projectData } from '../lib/projectData';

const EMPTY_VALUES = {
  fullName: '',
  email: '',
  phone: '',
  requestedAmount: '',
  loanPurpose: '',
};

const FIELDS = [
  {
    name: 'fullName',
    label: 'Full name',
    type: 'text',
    placeholder: 'e.g. Elena Marsh',
    autoComplete: 'name',
  },
  {
    name: 'email',
    label: 'Email address',
    type: 'email',
    placeholder: 'e.g. elena.marsh@example.com',
    autoComplete: 'email',
    inputMode: 'email',
  },
  {
    name: 'phone',
    label: 'Phone number',
    type: 'tel',
    placeholder: 'e.g. +1 415 555 0134',
    autoComplete: 'tel',
    inputMode: 'tel',
  },
  {
    name: 'requestedAmount',
    label: 'Requested loan amount',
    type: 'text',
    placeholder: 'e.g. 18000',
    inputMode: 'decimal',
    prefix: '$',
    wide: true,
  },
  {
    name: 'loanPurpose',
    label: 'Loan purpose',
    type: 'textarea',
    placeholder: 'e.g. Home repair',
    wide: true,
  },
];

function validate(values) {
  const errors = {};
  if (!values.fullName.trim()) {
    errors.fullName = 'Enter your full name.';
  }
  if (!values.email.trim()) {
    errors.email = 'Enter your email address.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  const digits = values.phone.replace(/[^\d]/g, '');
  if (!values.phone.trim()) {
    errors.phone = 'Enter your phone number.';
  } else if (digits.length < 7 || digits.length > 15) {
    errors.phone = 'Enter a valid phone number.';
  }
  const amountRaw = values.requestedAmount.replace(/[,\s$]/g, '');
  if (!amountRaw) {
    errors.requestedAmount = 'Enter the amount you would like to request.';
  } else if (!/^\d+(\.\d{1,2})?$/.test(amountRaw) || Number(amountRaw) <= 0) {
    errors.requestedAmount = 'Enter a valid amount greater than zero.';
  }
  if (!values.loanPurpose.trim()) {
    errors.loanPurpose = 'Tell us what the loan is for.';
  }
  return errors;
}

function newApplicationId() {
  const stamp = Date.now().toString(36).toUpperCase();
  const salt = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `app-${stamp}-${salt}`;
}

function ApplyApplicationPanel() {
  const [values, setValues] = useState(EMPTY_VALUES);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [submittedApplicantName, setSubmittedApplicantName] = useState('');
  const [submittedAmount, setSubmittedAmount] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const formRef = useRef(null);

  const submitting = submitStatus === 'submitting';

  function handleChange(event) {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    if (submitStatus === 'error') setSubmitStatus('idle');
  }

  async function submit() {
    if (submitting) return;
    const errors = validate(values);
    setFieldErrors(errors);
    setErrorMessage('');
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      setSubmitStatus('idle');
      const firstField = formRef.current
        ? formRef.current.querySelector(`[name="${errorKeys[0]}"]`)
        : null;
      if (firstField && typeof firstField.focus === 'function') firstField.focus();
      return;
    }

    setSubmitStatus('submitting');
    const amountRaw = values.requestedAmount.replace(/[,\s$]/g, '');
    try {
      const result = await projectData.create('loan_applications', {
        id: newApplicationId(),
        fullName: values.fullName.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        requestedAmount: amountRaw,
        loanPurpose: values.loanPurpose.trim(),
        submittedAt: new Date().toISOString(),
      });
      const savedName =
        result && typeof result === 'object' && result.fullName
          ? result.fullName
          : values.fullName.trim();
      setSubmittedApplicantName(savedName);
      setSubmittedAmount(amountRaw);
      setValues(EMPTY_VALUES);
      setSubmitStatus('success');
    } catch (err) {
      setErrorMessage(
        (err && err.message) || 'The application could not be sent. Your details are still here.'
      );
      setSubmitStatus('error');
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    submit();
  }

  function handleRetry() {
    submit();
  }

  const showForm = submitStatus !== 'success';

  return (
    <section className="aap-root" aria-labelledby="aap-panel-label">
      <div className="aap-rail" data-content-rail="primary">
        <div className={`aap-panel${submitStatus === 'success' ? ' is-success' : ''}`}>
          <span className="aap-figure" aria-hidden="true" />

          <div className="aap-head">
            <h2 className="aap-head-label" id="aap-panel-label">
              <span className="aap-head-numeral" aria-hidden="true">01</span>
              Your application
            </h2>
            <p className="aap-head-note">
              {showForm ? 'Five details, one request.' : 'Received.'}
            </p>
          </div>

          {submitStatus === 'success' && (
            <div className="aap-success" role="status" aria-live="polite">
              <span className="aap-success-mark" aria-hidden="true">
                <Check size={22} strokeWidth={2.2} />
              </span>
              <h3 className="aap-success-title">
                Thank you, <em>{submittedApplicantName}</em>.
              </h3>
              <p className="aap-success-copy">
                Montreaux has received your loan application
                {submittedAmount ? ` for $${Number(submittedAmount).toLocaleString('en-US')}` : ''}.
                It has been recorded and is ready to be reviewed.
              </p>
              <div className="aap-success-links">
                <a className="aap-text-link" href="/loans">
                  Return to Loans
                  <ArrowRight size={16} aria-hidden="true" />
                </a>
                <a className="aap-text-link" href="/">
                  Return to Landing
                  <ArrowRight size={16} aria-hidden="true" />
                </a>
              </div>
            </div>
          )}

          {showForm && (
            <form className="aap-form" ref={formRef} onSubmit={handleSubmit} noValidate>
              {submitStatus === 'error' && (
                <div className="aap-error" role="alert">
                  <p className="aap-error-title">
                    <AlertCircle size={17} aria-hidden="true" />
                    Application not sent
                  </p>
                  <p className="aap-error-copy">{errorMessage}</p>
                </div>
              )}

              {FIELDS.map((field) => {
                const invalid = Boolean(fieldErrors[field.name]);
                const errorId = `aap-error-${field.name}`;
                const commonProps = {
                  id: `aap-${field.name}`,
                  name: field.name,
                  value: values[field.name],
                  onChange: handleChange,
                  placeholder: field.placeholder,
                  required: true,
                  'aria-required': true,
                  'aria-invalid': invalid ? 'true' : undefined,
                  'aria-describedby': invalid ? errorId : undefined,
                  className:
                    field.type === 'textarea'
                      ? 'aap-textarea'
                      : 'aap-select aap-input',
                };
                return (
                  <div
                    className={`aap-field${invalid ? ' is-invalid' : ''}${field.wide ? ' aap-field--wide' : ''}`}
                    key={field.name}
                  >
                    <label className="aap-label" htmlFor={`aap-${field.name}`}>
                      {field.label}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea {...commonProps} rows={3} />
                    ) : field.prefix ? (
                      <span className="aap-amount-wrap">
                        <span className="aap-amount-prefix" aria-hidden="true">
                          {field.prefix}
                        </span>
                        <input
                          {...commonProps}
                          type="text"
                          inputMode={field.inputMode}
                          autoComplete="off"
                        />
                      </span>
                    ) : (
                      <input
                        {...commonProps}
                        type={field.type}
                        inputMode={field.inputMode}
                        autoComplete={field.autoComplete}
                      />
                    )}
                    {invalid && (
                      <span className="aap-error-msg" id={errorId}>
                        <AlertCircle size={15} aria-hidden="true" />
                        {fieldErrors[field.name]}
                      </span>
                    )}
                  </div>
                );
              })}

              <div className="aap-foot">
                <p className="aap-foot-note">
                  No account needed — your details stay with your request.
                </p>
                <div className="aap-foot-submit">
                  <button
                    type="submit"
                    className="aap-submit"
                    disabled={submitting}
                    aria-busy={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="aap-spinner" aria-hidden="true" />
                        Sending application…
                      </>
                    ) : (
                      <>
                        {submitStatus === 'error' ? 'Try again' : 'Send application'}
                        <ArrowRight size={18} aria-hidden="true" />
                      </>
                    )}
                  </button>
                  {submitStatus === 'error' && (
                    <div className="aap-error" role="status">
                      <p className="aap-error-copy">
                        Your details are kept exactly as entered.
                      </p>
                      <button
                        type="button"
                        className="aap-retry"
                        onClick={handleRetry}
                        disabled={submitting}
                      >
                        <RotateCw size={15} aria-hidden="true" />
                        Retry submission
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </form>
          )}

          <svg
            className="aap-arc"
            viewBox="0 0 600 26"
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M 2 20 C 120 2, 300 30, 598 10" />
          </svg>
        </div>
      </div>
    </section>
  );
}

export default ApplyApplicationPanel;